<!--
  ForgotPassword.svelte — Request a password-reset email.

  The user enters their email address.  On success, a reset code is
  sent and the UI shows a confirmation message with a link to the
  reset-password page (or the user can follow the email link directly).
-->
<script lang="ts">
	import { preventDefault } from 'svelte/legacy';

	import { Button } from 'flowbite-svelte';
	import AuthLayout from './AuthLayout.svelte';
	import { FormField, AlertMessage, LoadingOverlay, RoutedLink } from '../../components';
	import { forgotPassword, isValidEmail } from '../../AppNetwork';
	import { t, t2 } from '../../i18n';

	// ── State ─────────────────────────────────────────────────

	/** Email address entered by the user. */
	let email = $state('');
	/** Whether the request has been sent successfully. */
	let sent = $state(false);
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message. */
	let errorMessage = $state('');
	/** Field-level error for the email input. */
	let emailError = $state('');

	// ── Validation ────────────────────────────────────────────

	/** Validate the email field. */
	function validate(): boolean {
		emailError = '';
		errorMessage = '';
		/** Reject empty email field. */
		if (!email.trim()) {
			emailError = t2('Email is required.');
			return false;
		}
		/** Reject syntactically invalid email addresses. */
		if (!isValidEmail(email.trim())) {
			emailError = t2('Please enter a valid email address.');
			return false;
		}
		return true;
	}

	// ── Handler ───────────────────────────────────────────────

	/**
	 * Submit the forgot-password request.
	 * On success, show a confirmation message.
	 */
	async function handleSubmit(): Promise<void> {
		if (!validate()) return;

		loading = true;
		/** Send the reset request and show a confirmation on success. */
		try {
			await forgotPassword(email.trim());
			sent = true;
		} catch (err: any) {
			errorMessage = err?.message || t2('Failed to send reset email. Please try again.');
		} finally {
			loading = false;
		}
	}

	/** Handle Enter key. */
	function handleKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handleSubmit();
	}
</script>

<AuthLayout heading={$t("Forgot password?")} subheading={$t("Enter your email and we'll send you a reset code.")}>
	{#if loading}
		<LoadingOverlay />
	{/if}

	{#if errorMessage}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
	{/if}

	{#if sent}
		<!-- Success state -->
		<AlertMessage
			type="success"
			message={$t("If an account exists for {{email}}, you'll receive a reset code shortly.", { email })}
			dismissible={false}
		/>

		<div class="mt-6 text-center">
			<RoutedLink to="/reset-password?email={encodeURIComponent(email.trim())}">
				<span class="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
					>{$t("Enter reset code")}</span
				>
			</RoutedLink>
		</div>
	{:else}
		<!-- Request form -->
		<form onsubmit={preventDefault(handleSubmit)}>
			<FormField
				id="forgot-email"
				label={$t("Email")}
				type="email"
				placeholder={$t("you@example.com")}
				bind:value={email}
				error={emailError}
				required
				onkeypress={handleKeypress}
			/>

			<Button type="submit" class="w-full" disabled={loading}>{$t("Send reset code")}</Button>
		</form>
	{/if}

	<!-- Back to login -->
	<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
		<RoutedLink to="/login">
			<span class="font-medium text-primary-600 hover:underline dark:text-primary-400">{$t("Back to sign in")}</span>
		</RoutedLink>
	</div>
</AuthLayout>
